import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { msalInstance } from './msalConfig.js';

const GRAPH_PHOTO_URL = 'https://graph.microsoft.com/v1.0/me/photo/$value';

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo blob'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Fetches the signed-in member's Microsoft 365 profile photo via Graph and returns
 * it as a data URL. Resolves to `null` when no photo is set, the account lacks the
 * `User.Read` consent, or Graph is unreachable — callers fall back to initials.
 * Never triggers an interactive prompt so it can't disrupt the app.
 */
async function fetchProfilePhoto(): Promise<string | null> {
  if (!msalInstance) {
    return null;
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  let accessToken: string;
  try {
    const result = await msalInstance.acquireTokenSilent({ scopes: ['User.Read'], account });
    accessToken = result.accessToken;
  } catch {
    // No silent Graph token (e.g. User.Read not consented). Skip the photo.
    return null;
  }

  const response = await fetch(GRAPH_PHOTO_URL, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!response.ok) {
    // 404 = the member has no photo; any other error → fall back to initials.
    return null;
  }

  const blob = await response.blob();
  return blobToDataUrl(blob);
}

/** React Query hook exposing the member's Graph profile photo (data URL or null). */
export function useProfilePhoto(): UseQueryResult<string | null> {
  const account = msalInstance?.getActiveAccount() ?? msalInstance?.getAllAccounts()[0];
  return useQuery({
    queryKey: ['graph', 'me', 'photo', account?.homeAccountId ?? 'anonymous'],
    queryFn: fetchProfilePhoto,
    enabled: Boolean(account),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}
